import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/functions";
import "firebase/compat/storage";
import { navigate } from "gatsby-link";
import initializeStripe from "../Stripe/initialiseStripe";
import app from "./app";
import functionsDebugger from "./functionsDebug";

class Firebase {
  constructor() {
    if (!firebaseInstance) {
      this.auth = app.auth();
      this.db = app.firestore();
      this.functions = app.app().functions("europe-west2");
      
      // Only use emulator if explicitly enabled and in development
      // Comment out the next lines if you're not using the Firebase emulators
      if (process.env.GATSBY_ENVIRONMENT == "development" && process.env.GATSBY_USE_FUNCTIONS_EMULATOR === "true") {
        this.functions.useEmulator("localhost", 5001);
        console.log("✅ Using Functions Emulator at localhost:5001");
        
        // Firestore emulator is commented out - using production Firestore
        // this.db.useEmulator("localhost", 8080);
      } else {
        console.log("⚠️ NOT using Functions Emulator - calling PRODUCTION functions");
        console.log("   GATSBY_ENVIRONMENT:", process.env.GATSBY_ENVIRONMENT);
        console.log("   GATSBY_USE_FUNCTIONS_EMULATOR:", process.env.GATSBY_USE_FUNCTIONS_EMULATOR);
      }

      this.storage = app.storage();
    }
  }

  async createCheckoutSession({ uid }) {
    const checkoutSessionRef = await this.db
      .collection("users")
      .doc(uid)
      .collection("checkout_sessions")
      .add({
        price: "price_1K5Wo8FYlaVPK4IAT4asWCrf",
        success_url: window.location.origin,
        cancel_url: window.location.origin,
      });

    checkoutSessionRef.onSnapshot(async (snap) => {
      const { sessionId } = snap.data();
      if (sessionId) {
        const stripe = await initializeStripe();
        stripe.redirectToCheckout({ sessionId });
      }
    });
  }

  async submitStation({
    stationId,
    tickedPoints,
    percentage,
    breakdown,
    stationTitle,
    stationType,
    specialty,
  }) {
    const functionRef = this.functions.httpsCallable("submitStation");
    const response = await functionRef({
      stationId,
      tickedPoints,
      percentage,
      breakdown,
      stationTitle,
      stationType,
      specialty,
    });
    return response;
  }

  async submitPeerStation({
    shortId,
    stationId,
    tickedPoints,
    percentage,
    breakdown,
    email,
    stationTitle,
    stationType,
    specialty,
  }) {
    const functionRef = this.functions.httpsCallable("submitPeerStation");
    const response = await functionRef({
      shortId,
      stationId,
      tickedPoints,
      percentage,
      breakdown,
      email,
      stationTitle,
      stationType,
      specialty,
    });
    return response;
  }

  async retrieveStationsData() {
    const functionRef = this.functions.httpsCallable("retrieveStationsData");
    let stations;
    await functionRef().then((response) => {
      stations = response;
    });
    return stations;
  }

  async retrieveStationAttempts({ stationId }) {
    const functionRef = this.functions.httpsCallable("retrieveStationAttempts");
    let stations;
    await functionRef({ stationId }).then((response) => {
      stations = response;
    });
    return stations;
  }

  async logPage({ page_title }) {
    const functionRef = this.functions.httpsCallable("logPageOpened");
    await functionRef({
      page_title,
    });
  }

  async customerPortalRedirect() {
    var functions = app.app().functions("europe-west2");
    const functionRef = functions.httpsCallable(
      "ext-firestore-stripe-payments-createPortalLink"
    );
    const { data } = await functionRef({
      returnUrl: window.location.origin,
    });
    window.location.assign(data.url);
  }

  async isUserPremium() {
    await this.auth.currentUser.getIdToken(true);
    const decodedToken = await this.auth.currentUser.getIdTokenResult();

    return decodedToken.claims.stripeRole ? true : false;
  }

  async updateProfile({ firstname, lastname, userId }) {
    const users = this.db.collection("users");
    await users.doc(userId).set(
      {
        firstname: firstname,
        lastname: lastname,
      },
      { merge: true }
    );
    window.location.reload();

    return;
  }

  async getServerTime() {
    const time = await this.functions.httpsCallable("getServerTime");
    return time();
  }

  async testFunction(data = { test: true }) {
    console.log("🔵 Calling testFunction with data:", data);
    const testCallable = this.functions.httpsCallable("testFunction");
    try {
      const result = await testCallable(data);
      console.log("✅ testFunction returned:", result);
      return result;
    } catch (error) {
      console.error("❌ testFunction error:", error);
      throw error;
    }
  }


  async register({
    email,
    password,
    firstname,
    lastname,
    university,
    mailingList,
  }) {
    const createProfileCallable =
      this.functions.httpsCallable("createUserProfile");
    
    // Create user account first
    const userCredential = await this.auth.createUserWithEmailAndPassword(email, password);
    const user = userCredential.user;
    
    console.log("🔵 Calling createUserProfile function with data:", {
      email,
      firstname,
      lastname,
      university,
      mailingList
    });
    
    // Create user profile in Firestore
    try {
      const result = await createProfileCallable({
        email,
        firstname,
        lastname,
        university,
        mailingList,
      });
      console.log("✅ createUserProfile function returned:", result);
    } catch (error) {
      console.error("❌ createUserProfile function error:", error);
      console.error("❌ Error code:", error.code);
      console.error("❌ Error message:", error.message);
      throw error; // Re-throw so registration fails
    }
    
    // Create user profile in Firestore
    
    // Send verification email - this uses production Firebase Auth
    // Note: Email verification works with production Auth even when using Functions emulator
    try {
      await user.sendEmailVerification({
        url: `${window.location.origin}/verifyEmail`,
      });
      console.log("✅ Verification email sent successfully to:", email);
    } catch (error) {
      console.error("❌ Error sending verification email:", error);
      // Don't throw - user is created, just email failed
      // They can resend from the verifyEmail page
      // Don't re-throw - allow registration to succeed even if email fails
    }
    return
  }

  async login({ email, password }) {
    const logSignInTime = await this.functions.httpsCallable("logSignInTime");
    const userCredential = await this.auth.signInWithEmailAndPassword(
      email,
      password
    );
    logSignInTime();
  }

  async logout() {
    await this.auth.signOut();
  }
}

let firebaseInstance;

function getFirebaseInstance() {
  if (!firebaseInstance) {
    firebaseInstance = new Firebase();
    return firebaseInstance;
  } else if (firebaseInstance) {
    return firebaseInstance;
  } else {
    return null;
  }
}

export default getFirebaseInstance;
